<template>
    <div class="tab-pane active">
        <div v-if="!errorInResponse && locationsList.length > 0" class="table-wrapper no-print">
            <div v-show="searchKeyword || filteredLocationsList.length" class="time-window-label">
                {{ $t('checkIn.notCheckedInTab.timeWindowLabel', { beginTime, endTime }) }}
            </div>
            <table
                v-show="!searchKeyword || filteredLocationsList.length"
                id="forAT_table-NotCheckIn"
                class="common-table not-check-in"
            >
                <thead>
                    <tr class="tbl-header">
                        <th>{{ $t('checkIn.notCheckedInTab.table.columns.location') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in filteredLocationsList" :key="row.location_id">
                        <td>
                            {{
                                row.devices ? row.name.concat(' (' + row.devices.join(', ') + ')') : row.name
                            }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <h3 v-if="searchKeyword && !filteredLocationsList.length">
                {{ $t('common.searchNoResult', { val: searchKeyword }) }}
            </h3>
        </div>
        <div v-else class="data-empty no-print">
            <h3
                v-if="dataLoaded"
                v-text="
                    beginTime
                        ? $t('checkIn.notCheckedInTab.noResidents', { beginTime, endTime })
                        : $t('checkIn.notCheckedInTab.noDataLoaded')
                "
            ></h3>
        </div>
        <div class="print-only">
            <h4 v-if="searchKeyword && filteredLocationsList.length > 0">
                {{ $t('print.search', { val: searchKeyword }) }}
            </h4>
            <table v-if="locationsList.length > 0" class="table print-notcheckedin">
                <thead>
                    <tr class="tbl-header">
                        <th>
                            <p
                                v-text="$t('checkIn.notCheckedInTab.timeWindowLabel', { beginTime, endTime })"
                            ></p>
                            {{ $t('checkIn.notCheckedInTab.table.columns.location') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in printedLocationsList" :key="row.location_id">
                        <td>
                            {{
                                row.devices ? row.name.concat(' (' + row.devices.join(', ') + ')') : row.name
                            }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="data-empty print-only">
                <h3
                    v-text="
                        beginTime
                            ? $t('checkIn.notCheckedInTab.noResidents', { beginTime, endTime })
                            : $t('checkIn.notCheckedInTab.noDataLoaded')
                    "
                ></h3>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import mappingState from '../common/mappingState';
import { mapState, mapActions, mapMutations } from 'vuex';
import { errorHandler, sortLocations, ifIsNotCanceled, formatTime } from '../common/helpers';
import { getCancelToken } from '../../api';
import { ROUTE_NAMES, SUCCESS } from '../../constants';
import { resourceCancelNCICalls } from '../../api/resources';

export default {
    mixins: [mappingState],

    data() {
        return {
            dataLoaded: false,
            errorInResponse: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState({
            beginTime: state => (state.checkin.beginTime ? formatTime(state.checkin.beginTime) : null),
            endTime: state => (state.checkin.endTime ? formatTime(state.checkin.endTime) : null),
            locationsList: state => sortLocations(state.checkin.locationsList),
        }),

        filteredLocationsList() {
            let keyword = this.searchKeyword;
            return keyword
                ? this.locationsList.filter(
                      item =>
                          item.name.toLowerCase().indexOf(keyword) !== -1 ||
                          (item.devices || []).some(device => device.toLowerCase().indexOf(keyword) !== -1)
                  )
                : this.locationsList;
        },

        printedLocationsList() {
            return this.filteredLocationsList.length > 0 ? this.filteredLocationsList : this.locationsList;
        },
    },

    mounted() {
        this.loadNotCheckedIn();
    },

    created() {
        this.configButton({
            buttonName: 'cancelNCICallsButton',
            action: () => {
                this.cancelNotCheckedInCalls();
            },
            disabled: () => this.filteredLocationsList.length === 0,
        });
        this.updateData(() => {
            return this.loadNotCheckedIn();
        });
    },

    updated() {
        this.setSearchEnabled(this.locationsList.length);
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        ...mapActions({
            loadNotCheckedInInfo: 'checkin/loadNotCheckedInInfo',
        }),

        /**
         * Load information for not checked in tab
         */
        loadNotCheckedIn() {
            this.showLoading(true);
            this.errorInResponse = false;
            return this.loadNotCheckedInInfo({ id: this.userAreaId, cancelToken: this.cancelToken.token })
                .then(() => {
                    this.dataLoaded = true;
                    this.showLoading(false);
                })
                .catch(
                    ifIsNotCanceled({
                        callback: error => {
                            this.errorInResponse = true;
                            errorHandler(error.status, ROUTE_NAMES.CHECK_IN__NOT_CHECKED_IN);
                        },
                    })
                );
        },
        cancelNotCheckedInCalls() {
            return this.confirmation({
                confirmTitle: this.$t('checkIn.notCheckedInTab.cancelNCICalls'),
                confirmBody: this.$t('checkIn.notCheckedInTab.cancelNCICallsConfirm'),
            }).then(r => {
                if (r) {
                    this.showLoading(true);
                    return resourceCancelNCICalls({
                        method: 'post',
                        id: this.userAreaId,
                        cancelToken: this.cancelToken.token,
                    })
                        .then(() => {
                            this.showLoading(false);
                            this.popup({
                                type: SUCCESS,
                                header: this.$t('common.success'),
                                text: this.$t('checkIn.notCheckedInTab.cancelMessage'),
                            });
                        })
                        .catch(ifIsNotCanceled());
                }
            });
        },
    },
};
</script>
